import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Grid3X3, LayoutGrid } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import ContactSheet from "@/components/ContactSheet";
import CaseStudyCard from "@/components/CaseStudyCard";
import type { CaseStudyCardData } from "@/components/CaseStudyCard";
import FooterSection from "@/components/FooterSection";
import { supabase } from "@/integrations/supabase/client";
import { useIsMobile } from "@/hooks/use-mobile";
import SEO from "@/components/SEO";

interface Study {
  slug: string;
  title: string;
  subtitle: string | null;
  category: string | null;
  cover_image_url: string | null;
  logo_url: string | null;
  tags: string[] | null;
}

const CaseStudies = () => {
  const [studies, setStudies] = useState<Study[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState<string>("All");
  const [columns, setColumns] = useState<3 | 4>(() => {
    const saved = localStorage.getItem("caseStudiesColumns");
    return (saved === "3" ? 3 : 4) as 3 | 4;
  });
  const [page, setPage] = useState(1);
  const isMobile = useIsMobile();
  const scrollRef = useRef<HTMLDivElement>(null);
  const autoTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const isPausedRef = useRef(false);

  const scrollByCard = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const atEnd = el.scrollLeft >= el.scrollWidth - el.clientWidth - 10;
    if (atEnd) {
      el.scrollTo({ left: 0, behavior: "smooth" });
    } else {
      el.scrollBy({ left: 300, behavior: "smooth" });
    }
  }, []);

  const startAutoPlay = useCallback(() => {
    if (autoTimerRef.current) clearInterval(autoTimerRef.current);
    autoTimerRef.current = setInterval(() => {
      if (isPausedRef.current) return;
      scrollByCard();
    }, 5000);
  }, [scrollByCard]);

  const pauseAuto = useCallback(() => { isPausedRef.current = true; }, []);
  const resumeAuto = useCallback(() => {
    isPausedRef.current = false;
    startAutoPlay();
  }, [startAutoPlay]);

  useEffect(() => {
    if (!isMobile) return;
    startAutoPlay();
    return () => { if (autoTimerRef.current) clearInterval(autoTimerRef.current); };
  }, [isMobile, startAutoPlay]);

  useEffect(() => {
    const fetch = async () => {
      const { data, error } = await supabase.
      from("case_studies").
      select("slug, title, subtitle, category, cover_image_url, logo_url, tags").
      order("display_order", { ascending: true });
      if (!error && data) setStudies(data);
      setLoading(false);
    };
    fetch();
  }, []);

  const categories = useMemo(() => {
    const cats = new Set<string>();
    studies.forEach((s) => s.category && cats.add(s.category));
    return ["All", ...Array.from(cats)];
  }, [studies]);

  const categoryCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    studies.forEach((s) => {
      if (s.category) {
        counts[s.category] = (counts[s.category] || 0) + 1;
      }
    });
    return counts;
  }, [studies]);

  const filtered = useMemo(
    () =>
    activeFilter === "All" ?
    studies :
    studies.filter((s) => s.category === activeFilter),
    [studies, activeFilter]
  );

  const paginatedMobile = useMemo(
    () => filtered.slice(0, page * 10),
    [filtered, page]
  );

  // Reset page when filter changes
  useEffect(() => {
    setPage(1);
  }, [activeFilter]);

  // Persist column preference
  useEffect(() => {
    localStorage.setItem("caseStudiesColumns", columns.toString());
  }, [columns]);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <SEO
        title="Case Studies"
        description="Dive into the process behind selected projects — strategy, design decisions, and outcomes."
        canonical="/case-studies"
      />
      {/* Top nav */}
      <header className="flex items-center justify-between px-5 md:px-8 py-4 border-b border-border">
         <Link
           to="/"
           className="flex items-center gap-2 font-body text-ts-2xs tracking-[0.15em] uppercase opacity-40 hover:opacity-100 transition-opacity">

           <ArrowLeft size={12} />
           Back
         </Link>
         <ContactSheet>
           <button className="font-body text-ts-2xs tracking-[0.15em] uppercase opacity-40 hover:opacity-100 transition-opacity">
             Work With Us
           </button>
        </ContactSheet>
      </header>

      <div className="px-5 md:px-8 lg:px-12 py-8 md:py-12">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
          className="mb-8">

           <h1 className="font-display text-[clamp(2rem,5vw,4rem)] italic leading-[0.95] tracking-tight">
             Case Studies
           </h1>
           <p className="font-body text-ts-2xs text-muted-foreground mt-2 max-w-md">
            Dive into the process behind selected projects — strategy, design decisions, and outcomes.
          </p>
        </motion.div>

        {/* Filter bar + column switcher */}
        <div className="flex items-center justify-between mb-8 border-b border-border pb-4">
          <div className="flex flex-wrap gap-2">
             {categories.map((cat) =>
               <button
                 key={cat}
                 onClick={() => setActiveFilter(cat)}
                 className={`font-body text-ts-2xs tracking-[0.15em] uppercase px-3 py-1.5 border transition-all duration-300 ${
                   activeFilter === cat
                     ? "bg-foreground text-background border-foreground"
                     : "border-border text-muted-foreground hover:text-foreground hover:border-foreground"
                 }`}
               >
                {cat} {cat !== "All" && `(${categoryCounts[cat] || 0})`}
              </button>
            )}
          </div>
          <div className="hidden lg:flex items-center gap-1">
            <button
              onClick={() => setColumns(3)}
              className={`p-1.5 transition-opacity ${columns === 3 ? "opacity-100" : "opacity-30 hover:opacity-60"}`}
              aria-label="3 columns"
            >
              <Grid3X3 size={14} />
            </button>
            <button
              onClick={() => setColumns(4)}
              className={`p-1.5 transition-opacity ${columns === 4 ? "opacity-100" : "opacity-30 hover:opacity-60"}`}
              aria-label="4 columns"
            >
              <LayoutGrid size={14} />
            </button>
          </div>
        </div>

        {/* Grid / Carousel */}
         {loading ? (
           <p className="font-body text-ts-2xs text-muted-foreground">Loading…</p>
        ) : isMobile ? (
          /* Mobile: horizontal scroll strip (same style as "More Work" carousel) */
          <div>
            <div
              ref={scrollRef}
              onTouchStart={pauseAuto}
              onTouchEnd={() => setTimeout(resumeAuto, 8000)}
              className="flex gap-4 overflow-x-auto pb-6 snap-x snap-mandatory -mx-5 px-5"
              style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
            >
              {paginatedMobile.map((study, i) => {
                const mapped: CaseStudyCardData = {
                  slug: study.slug,
                  title: study.title,
                  subtitle: study.subtitle,
                  category: study.category,
                  image: study.cover_image_url || "",
                  logo: study.logo_url || "",
                };
                return (
                  <motion.div
                    key={study.slug}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.06, duration: 0.4 }}
                    className="flex-shrink-0 w-[75vw] max-w-[320px] snap-center"
                  >
                    <CaseStudyCard study={mapped} />
                  </motion.div>
                );
              })}
            </div>

            {/* Show more */}
             {filtered.length > page * 10 && (
               <div className="flex justify-center mt-2">
                 <button
                   onClick={() => setPage((p) => p + 1)}
                   className="font-body text-ts-2xs tracking-[0.15em] uppercase border border-border px-4 py-2 hover:bg-foreground hover:text-background transition-colors"
                 >
                  Show more
                </button>
              </div>
            )}
          </div>
        ) : (
          /* Desktop: grid */
          <motion.div
            layout
            className={`grid grid-cols-1 sm:grid-cols-2 ${columns === 3 ? "lg:grid-cols-3" : "lg:grid-cols-4"} gap-6 md:gap-8 mb-0`}
          >
            <AnimatePresence mode="popLayout">
              {filtered.map((study) => {
                const mapped: CaseStudyCardData = {
                  slug: study.slug,
                  title: study.title,
                  subtitle: study.subtitle,
                  category: study.category,
                  image: study.cover_image_url || "",
                  logo: study.logo_url || "",
                };
                return (
                  <motion.div
                    key={study.slug}
                    layout
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    transition={{ duration: 0.35 }}
                  >
                    <CaseStudyCard study={mapped} />
                  </motion.div>
                );
              })}
            </AnimatePresence>
          </motion.div>
        )}
      </div>

      <FooterSection />
    </div>
  );
};

export default CaseStudies;